import React, { CSSProperties } from 'react';
import {
  AudioContent,
  JsonContent,
  JsonContentNode,
  JsonContentType,
  JsonContentValidationMessage,
  JsonImgNode
} from './';
import { QueueUxPreferences, SubjectImagePreferences } from 'services';
import { getTheme, IStackStyles, IStyle, mergeStyles, Stack } from '@fluentui/react';
import { HeaderValueType } from './jsonContentTypes/HeaderValueType';
import { HeaderType } from './jsonContentTypes/HeaderType';
import { IframeType } from './jsonContentTypes/IframeType';
import { TextType } from './jsonContentTypes/TextType';
import { UrlType } from './jsonContentTypes/UrlType';
import { TableType } from './jsonContentTypes/TableType';
import { TooltipText } from '../TooltipText';
import { ImageCarouselType } from './jsonContentTypes/ImageCarouselType';
import { ImageUrlType } from './jsonContentTypes/ImageUrlType';
import { ImageBytesType } from './jsonContentTypes/ImageBytesType';
import { SectionHeaderType } from './jsonContentTypes/SectionHeader';
import { VideoContent } from './VideoContent';

interface JsonFrameProps {
  value: JsonContent;
  focusText: string;
  cssLink: string | undefined;
  uxPreferences: QueueUxPreferences;
  isSingleView?: boolean;
  oneCase?: boolean;
  settings?: SubjectImagePreferences;
  className?: string;
  onClick?: () => void;
  isZoomInActive?: boolean;
  checked?: boolean;
}

export interface JsonImgNodeProps extends JsonImgNode {
  isGridView: boolean;
  isZoomInActive?: boolean;
  subjectImagePreferences?: SubjectImagePreferences;
  extraHeight?: number; // Allow an extra margin for content above/below the image (like next/prev controls)
  uxPreferences?: QueueUxPreferences;
}

export const jsonImageCaptionOverlayStyle = (palette): CSSProperties => {
  const jsonImageCaptionOverlayStyle: CSSProperties = {
    opacity: 0.6,
    backgroundColor: palette.black,
    height: 40,
    bottom: 0,
    zIndex: 1
  };
  return jsonImageCaptionOverlayStyle;
};

export const jsonImageCaptionStyle = (palette, fonts, color: string, textType: string): CSSProperties => {
  const jsonImageCaptionStyle: CSSProperties = {
    color: palette[color],
    fontSize: fonts[textType].fontSize,
    lineHeight: fonts[textType].lineHeight,
    fontWeight: 600,
    fontFamily: fonts[textType].fontFamily
  };
  return jsonImageCaptionStyle;
};

export const jsonImageCaptionOverlayClass = 'w-100 d-block position-absolute';
export const jsonImageCaptionWrapperClass = (isZoomInActive?: boolean): string => {
  const jsonImageCaptionWrapperClass = mergeStyles(
    {
      bottom: 0,
      zIndex: 1
    },
    'w-100 d-block pb-2 position-absolute ' + (isZoomInActive ? 'pl-3' : 'pl-2')
  );
  return jsonImageCaptionWrapperClass;
};

export const empty = (value): boolean => {
  return value == '' || value === null || value === undefined || !value ? true : false;
};

export const scrollStyle: { [key: string]: IStyle } = {
  '&::-webkit-scrollbar': {
    width: 28,
    height: 8
  },
  '&::-webkit-scrollbar-thumb': {
    height: 6,
    borderColor: 'rgba(0, 0, 0, 0)',
    borderWidth: 9,
    borderStyle: 'solid',
    backgroundClip: 'padding-box',
    borderRadius: 25,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    boxShadow: 'inset -1px -1px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 0px rgba(0, 0, 0, 0.05)'
  },
  '&::-webkit-scrollbar-button': {
    width: 0,
    height: 0,
    display: 'none'
  },
  '&::-webkit-scrollbar-corner': {
    backgroundColor: 'transparent'
  }
};

const jsonContentWrapperStyles: Partial<IStackStyles> = {
  root: {
    // TODO: Make Scrollbars look pretty
    // https://microsoft.visualstudio.com/Xbox/_workitems/edit/36827289
    // selectors: scrollStyle,
    // paddingRight: 27 // Make room for the scrollbar
    // overflow: 'scroll'
  }
};

const hrStryle: React.CSSProperties = { borderTopColor: '#cecece', height: 1 };

// Renders the element based on the key and structure
function renderJsonOutput(
  json: JsonContentNode,
  focusText: string,
  isGridView: boolean,
  uxPreferences: QueueUxPreferences,
  settings?: SubjectImagePreferences,
  isZoomInActive?: boolean,
  checked?: boolean
): JSX.Element {
  switch (json.type) {
    case JsonContentType.br:
      return <br />;
    case JsonContentType.hr:
      return <hr style={hrStryle} className="mx-3 my-3" />;
    case JsonContentType.headerValue:
      return <HeaderValueType header={json.value.header} value={json.value.value} isZoomInActive={isZoomInActive} />;
    case JsonContentType.header:
      return <HeaderType val={json.value} isZoomInActive={isZoomInActive} />;
    case JsonContentType.sectionHeader:
      return <SectionHeaderType val={json.value} isZoomInActive={isZoomInActive} />;
    case JsonContentType.iframe:
      return <IframeType val={json.value} uxPreferences={uxPreferences} settings={settings} />;
    case JsonContentType.table:
      return <TableType val={json.value} focusText={focusText} isZoomInActive={isZoomInActive} />;
    case JsonContentType.text:
      return <TextType val={json.value} isZoomInActive={isZoomInActive} settings={settings} />;
    case JsonContentType.audioUrl:
      return <AudioContent src={json.value.value} />;
    case JsonContentType.url:
      return <UrlType val={json.value} />;
    case JsonContentType.imageCarousel:
      return (
        <ImageCarouselType
          isGridView={isGridView}
          val={json.value}
          focusText={focusText}
          isZoomInActive={isZoomInActive}
          subjectImagePreferences={settings}
          uxPreferences={uxPreferences}
          checked={checked}
        />
      );
    case JsonContentType.imageUrl:
      return (
        <ImageUrlType
          isGridView={isGridView}
          value={json.value.value}
          caption={json.value.caption}
          isZoomInActive={isZoomInActive}
          subjectImagePreferences={settings}
          uxPreferences={uxPreferences}
        />
      );
    case JsonContentType.imageBytes:
      return (
        <ImageBytesType
          isGridView={isGridView}
          value={json.value.value}
          caption={json.value.caption}
          isZoomInActive={isZoomInActive}
          subjectImagePreferences={settings}
          uxPreferences={uxPreferences}
        />
      );
    case JsonContentType.video:
      return (
        <VideoContent
          isGridView={isGridView}
          src={json.value.url}
          frames={json.value.frames}
          subjectImagePreferences={settings}
          uxPreferences={uxPreferences}
        />
      );
    default:
      return (
        <div className={isZoomInActive ? 'my-3' : 'mt-4 pt-3'}>
          <JsonContentValidationMessage
            message={`${TooltipText.unsupportedTypeText}${json.type}`}
            validationMessageWrapperClass={'ml-3'}
          />
        </div>
      );
  }
}

// Renders the output based on passed in JSON
// Loops the elements and renders them
function createJsonOutput(props: JsonFrameProps): JSX.Element {
  const isEmpty = empty(props.value.output);
  const outputValidationMessageClass = mergeStyles(
    {
      minHeight: props.isZoomInActive ? 500 : ''
    },
    'mt-4 ml-3'
  );

  /*
      when we are in a jsonContent queue
      and we are in a grid view (i.e. reviewing more than one case at once)

      - always show the first element in the json content list
      - if there are additional elements, hide them in the grid view, unless they are 'simple elements' (text, header, or headerValue)
      - show all elements when a card is maximized

      In general we advise against using grid view if you have multiple non-simple elements.
      */
  let notEmptyJsonOutput;

  if (props.isZoomInActive || (props.isSingleView && props.oneCase)) {
    // For zoom or single view we show everything
    notEmptyJsonOutput = props.value.output.map(element => {
      return renderJsonOutput(
        element,
        props.focusText,
        false,
        props.uxPreferences,
        props.settings,
        props.isZoomInActive,
        props.checked
      );
    });
  } else {
    // for grid view we always show the first element and all gridViewSafeElementTypes
    const gridViewSafeElementTypes = [JsonContentType.text, JsonContentType.header, JsonContentType.headerValue];
    notEmptyJsonOutput = props.value.output.map((element, index) => {
      if (index === 0 || gridViewSafeElementTypes.includes(element.type)) {
        return renderJsonOutput(
          element,
          props.focusText,
          true,
          props.uxPreferences,
          props.settings,
          props.isZoomInActive,
          props.checked
        );
      }
    });
  }

  return (
    <div className="w-100 h-100 jsonContent">
      {props.cssLink && <link rel="stylesheet" href={props.cssLink} />}
      <link rel="stylesheet" href="css/bootstrap.min.css" />
      <link rel="stylesheet" href="css/root.css" />
      {!isEmpty ? (
        notEmptyJsonOutput
      ) : (
        <div className="pt-3">
          <JsonContentValidationMessage
            message={TooltipText.jsonContentTypeOutputValidationMessage}
            validationMessageWrapperClass={outputValidationMessageClass}
          />
        </div>
      )}
    </div>
  );
}

// Outputs the rendered content
// Adds enough spacing at the bottom of the content to account
// for a collapsed details pane, allowing all content to be viewed
export const JsonFrame: React.FunctionComponent<JsonFrameProps> = (props): JSX.Element => {
  const { palette } = getTheme();

  const output: JSX.Element = createJsonOutput(props);

  return (
    <div
      className={`w-100 h-100 ${props.className || ''}`}
      style={{ background: palette.white, borderRadius: props.isZoomInActive ? 6 : 0 }}
    >
      <Stack
        className={'w-100 h-100 overflow-overlay' + (props.isZoomInActive ? 'rounded' : '')}
        styles={jsonContentWrapperStyles}
        onClick={props.onClick}
        onLoad={(): void => {
          const reportedText = document.getElementById('reportedText');
          if (reportedText) {
            reportedText.scrollIntoView({ block: 'center', inline: 'center' });
          }
        }}
      >
        {output}
      </Stack>
    </div>
  );
};
